import React, { useState, useEffect, useRef, memo } from 'react';
import { AxiosResponse } from 'axios';
import { SelectOption } from "dto/components/app-select-input-dto";

interface AppSelectLangInputProps {
  name: string;
  placeholder?: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loadOptions?: () => Promise<AxiosResponse<{ content: SelectOption[] }>> | Promise<SelectOption[]> | SelectOption[];
  customWidth?: boolean;
  disabled?: boolean;
  className?: string;
}

const AppSelectLangInput: React.FC<AppSelectLangInputProps> = ({
  name,
  placeholder = 'Select language',
  value,
  onChange,
  loadOptions,
  customWidth = false,
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load options on mount or when loadOptions changes
  useEffect(() => {
    const fetchOptions = async () => {
      if (loadOptions) {
        setLoading(true);
        try {
          const result = await loadOptions();
          
          // Handle different response types
          let optionsData: SelectOption[] = [];
          
          if (Array.isArray(result)) {
            // Direct array of options
            optionsData = result;
          } else if (result && typeof result === 'object' && 'data' in result) {
            // AxiosResponse with data.content structure
            const axiosResult = result as AxiosResponse<{ content: SelectOption[] }>;
            if (axiosResult.data && Array.isArray(axiosResult.data.content)) {
              optionsData = axiosResult.data.content;
            }
          } else if (result && typeof result === 'object' && 'content' in result) {
            // Direct content structure
            const contentResult = result as { content: SelectOption[] };
            if (Array.isArray(contentResult.content)) {
              optionsData = contentResult.content;
            }
          }
          
          setOptions(optionsData);
        } catch (error) {
          console.error('Error loading language options:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOptions();
  }, [loadOptions]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get display value
  const getDisplayValue = () => {
    if (value) {
      const selectedOption = options.find(opt => opt.value === value);
      return selectedOption?.label || value;
    }
    return '';
  };

  // Handle option selection
  const handleOptionClick = (optionValue: string) => {
    const event = {
      target: {
        name,
        value: optionValue
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(event);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle input change for search
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    if (value) {
      setSearchTerm('');
    }
  };

  // Handle clear selection
  const handleClear = () => {
    const event = {
      target: {
        name,
        value: ''
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(event);
    setSearchTerm('');
  };

  const containerClassName = `app-select-lang-input ${customWidth ? 'app-select-lang-input--custom-width' : ''} ${className}`;

  return (
    <div className={containerClassName} ref={dropdownRef}>
      <div className={`select-container ${disabled ? 'select-container--disabled' : ''}`}>
        <span className="select-leading-icon" aria-hidden="true">
          {/* Simple globe svg */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 21c4.971 0 9-4.029 9-9s-4.029-9-9-9-9 4.029-9 9 4.029 9 9 9Z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M3 12h18M12 3c2.485 2.41 3.75 5.41 3.75 9S14.485 19.59 12 21M12 3C9.515 5.41 8.25 8.41 8.25 12S9.515 18.59 12 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          className="select-input"
          value={isOpen ? searchTerm : getDisplayValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        
        <div className="select-icons">          
          <button
            type="button"
            className={`dropdown-arrow ${isOpen ? 'dropdown-arrow--open' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            aria-label="Toggle dropdown"
          >
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="select-dropdown">
          {loading ? (
            <div className="select-option select-option--loading">Loading...</div>
          ) : filteredOptions.length > 0 ? (
            <ul className="select-options">
              {filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className={`select-option ${value === option.value ? 'select-option--selected' : ''}`}
                  onClick={() => handleOptionClick(option.value)}
                >
                  {option.label}
                  {value === option.value && (
                    <svg className="check-icon" width="16" height="12" viewBox="0 0 16 12" fill="none">
                      <path d="M1 6L6 11L15 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="select-option select-option--empty">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(AppSelectLangInput);